<template>
  <div ref="reference">
    <slot />

    <teleport v-if="label && !isMobile" to="body">
      <gv-transition-dropdown>
        <div v-show="open" ref="tooltip" :class="`absolute z-tooltip ${size?.container}`">
          <slot name="tooltip">
            <div ref="tooltip" :class="`${backgroundStyle} px-3 py-2 rounded-lg`">
              <gv-type
                variant="body-sm"
                scheme="custom"
                :class="`font-medium whitespace-pre text-center leading-tight ${textStyle} ${size?.type}`"
              >
                {{ label }}
              </gv-type>
            </div>
          </slot>
          <div
            v-if="props.floatingUiOptions?.arrow ?? DEFAULT_OPTIONS?.arrow"
            ref="arrow"
            :class="`${backgroundStyle} h-3 w-3 rotate-45 absolute z-tooltip`"
          ></div>
        </div>
      </gv-transition-dropdown>
    </teleport>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useElementHover } from '@vueuse/core';

import { useFloatingUi, UseFloatingUiOptions, DEFAULT_OPTIONS } from '../../composables';
import { Scheme, Size } from './types';
import { isMobile } from '@/common/helpers';

const props = withDefaults(
  defineProps<{
    floatingUiOptions?: UseFloatingUiOptions;
    label?: string;
    allowHoverTooltip?: boolean;
    scheme?: Scheme;
    size?: Size;
    overrideHover?: {
      value: boolean;
      default: undefined;
    };
  }>(),
  {
    scheme: 'dark',
    size: 'default',
    allowHoverTooltip: false,
  }
);

const backgroundStyles: Record<Scheme, string> = {
  dark: 'bg-primary-600',
  light: 'bg-white',
};
const backgroundStyle = computed(() => backgroundStyles[props.scheme]);
const textStyles: Record<Scheme, string> = {
  dark: 'text-white',
  light: 'text-black',
};

const size = computed(() => {
  return {
    sm: { container: 'max-w-sm', type: 'text-center' },
    md: { container: 'max-w-md', type: 'text-center' },
    xs: { container: 'max-w-xs', type: 'text-center' },
    default: { container: '', type: '' },
  }[props.size];
});

const textStyle = computed(() => textStyles[props.scheme]);

const reference = ref();
const tooltip = ref();
const arrow = ref();

const isHoveringReference = useElementHover(reference);
const isHoveringTooltip = useElementHover(tooltip);

const open = computed(() => {
  if (props.allowHoverTooltip) {
    return props.overrideHover ?? (isHoveringReference.value || isHoveringTooltip.value);
  }
  return props.overrideHover ?? isHoveringReference.value;
});

useFloatingUi(
  reference,
  tooltip,
  {
    ...DEFAULT_OPTIONS,
    placement: 'top',
    ...props.floatingUiOptions,
  },
  arrow
);
</script>
